import {
  WhatsAppMessage,
  ChatAnalysis,
  ParticipantStats,
  ResponseTimeMetrics,
  Decision,
  DecisionType,
  ConfidenceLevel,
  ParsingStats,
} from '@/types/whatsapp';
import { format } from 'date-fns';

/**
 * Context-aware keyword patterns with confidence levels.
 * Each pattern is more specific to reduce false positives.
 */
interface KeywordPattern {
  term: string;
  pattern: RegExp;
  confidence: ConfidenceLevel;
}

const KEYWORD_PATTERNS: Record<string, KeywordPattern[]> = {
  decisions: [
    { term: 'approved', pattern: /\b(is|has been|formally|officially|now)\s+approved\b/i, confidence: 'high' },
    { term: 'approved', pattern: /\bapproved\s+(the|this|for)\b/i, confidence: 'medium' },
    { term: 'confirmed', pattern: /\b(confirmed|confirm)\s+(the|that|this|it)/i, confidence: 'medium' },
    { term: 'agreed', pattern: /\b(we|team|all|everyone)\s+(have\s+)?agreed\b/i, confidence: 'medium' },
    { term: 'signed off', pattern: /\bsigned\s+off\b/i, confidence: 'high' },
    { term: 'authorized', pattern: /\bauthori[sz]ed\b/i, confidence: 'medium' },
  ],
  deadlines: [
    { term: 'deadline', pattern: /\bdeadline\s+(is|:)?\s*\d/i, confidence: 'high' },
    { term: 'deadline', pattern: /\bdeadline\s+(for|of)\b/i, confidence: 'medium' },
    { term: 'due', pattern: /\bdue\s+(by|on|date|:)\s/i, confidence: 'medium' },
    { term: 'due', pattern: /\bdue\s+(by|on)\s+\d/i, confidence: 'high' },
    { term: 'until', pattern: /\buntil\s+\d{1,2}[/.-]\d{1,2}/i, confidence: 'medium' },
  ],
  costs: [
    { term: 'cost', pattern: /\b(total\s+)?cost\s+(is|of|:|was)\s*[£$€]?\d/i, confidence: 'high' },
    { term: 'quote', pattern: /\bquote\s+(for|of|is|:)\s*[£$€]?\d/i, confidence: 'high' },
    { term: 'price', pattern: /\bprice\s+(is|of|:)\s*[£$€]?\d/i, confidence: 'high' },
    { term: 'budget', pattern: /\bbudget\s+(is|of|:)\s*[£$€]?\d/i, confidence: 'medium' },
    { term: 'variation', pattern: /\bvariation\s+(of\s+)?[£$€]?\d/i, confidence: 'high' },
    { term: 'currency', pattern: /[£$€]\s*\d{1,3}(?:,\d{3})*(?:\.\d{2})?/i, confidence: 'medium' },
  ],
  issues: [
    { term: 'issue', pattern: /\b(major|critical|serious)\s+issue\b/i, confidence: 'high' },
    { term: 'issue', pattern: /\bissue\s+(with|on|at)\b/i, confidence: 'medium' },
    { term: 'problem', pattern: /\b(major|critical|serious)\s+problem\b/i, confidence: 'high' },
    { term: 'problem', pattern: /\bproblem\s+(with|on|at)\b/i, confidence: 'medium' },
    { term: 'concern', pattern: /\bconcern(ed)?\s+(about|with|that)\b/i, confidence: 'medium' },
    { term: 'risk', pattern: /\brisk\s+(of|that|is)\b/i, confidence: 'medium' },
    { term: 'blocked', pattern: /\b(is\s+)?blocked\s+(by|on|until)\b/i, confidence: 'high' },
  ],
  actions: [
    { term: 'action', pattern: /\baction\s+(item|required|needed)\b/i, confidence: 'high' },
    { term: 'task', pattern: /\btask\s+(for|assigned|to)\b/i, confidence: 'medium' },
    { term: 'todo', pattern: /\bto\s*-?\s*do\s*:/i, confidence: 'high' },
    { term: 'follow up', pattern: /\bfollow\s+up\s+(on|with|about)\b/i, confidence: 'medium' },
    { term: 'review', pattern: /\b(please\s+)?review\s+(the|this|and)\b/i, confidence: 'medium' },
  ],
};

/** Legacy simple keyword terms for broader matching */
const PROJECT_TERMS = {
  decisions: ['approved', 'agreed', 'confirmed', 'signed off', 'authorized'],
  deadlines: ['due by', 'deadline', 'due date', 'by', 'until'],
  costs: ['£', '$', '€', 'cost', 'price', 'quote', 'budget', 'variation', 'additional'],
  quantities: ['units', 'qty', 'quantity', 'm2', 'm3', 'tonnes'],
  issues: ['issue', 'problem', 'concern', 'risk', 'blocked'],
  delays: ['delay', 'postpone', 'late', 'behind', 'reschedule', 'push back'],
  actions: ['action', 'task', 'todo', 'follow up', 'review'],
} as const;

const DELAY_REASONS = {
  weather: ['rain', 'weather', 'storm', 'wind'],
  materials: ['delivery', 'material', 'supply', 'shortage'],
  labor: ['staff', 'worker', 'labour', 'resource'],
  equipment: ['equipment', 'machinery', 'plant', 'tool'],
  approvals: ['approval', 'permit', 'permission', 'sign-off'],
} as const;

const WORK_TYPES = {
  structural: ['steel', 'concrete', 'foundation', 'beam'],
  finishes: ['paint', 'finish', 'decoration', 'carpet'],
  services: ['electrical', 'plumbing', 'mechanical', 'hvac'],
  external: ['landscaping', 'paving', 'drainage', 'facade'],
} as const;

const NON_NOUNS = new Set([
  'the', 'a', 'an',
  'attached', 'media', 'image', 'video', 'audio', 'file',
  'omitted', 'message', 'sent', 'received', 'deleted',
  'i', 'you', 'he', 'she', 'it', 'we', 'they', 'me', 'him', 'her', 'us', 'them',
  'is', 'are', 'was', 'were', 'be', 'been', 'have', 'has', 'had',
  'do', 'does', 'did', 'will', 'would', 'can', 'could', 'should',
  'in', 'on', 'at', 'to', 'for', 'with', 'by', 'from', 'of',
  'and', 'or', 'but', 'if', 'when', 'while', 'because',
  'good', 'bad', 'big', 'small', 'new', 'old',
  'today', 'tomorrow', 'yesterday', 'now', 'later',
  'one', 'two', 'three', 'first', 'second', 'third',
  'hi', 'hello', 'hey', 'thanks', 'please', 'ok', 'okay',
  'need', 'want', 'get', 'take', 'make', 'see', 'look',
  'send', 'check', 'update', 'confirm', 'approve',
]);

const NOUN_ENDINGS = [
  'tion', 'ment', 'ence', 'ance', 'age', 'ity', 'ness',
  'ship', 'hood', 'ism', 'ing', 'er', 'or', 'ist',
];

const PROJECT_NOUNS = new Set([
  'concrete', 'steel', 'timber', 'brick', 'block', 'glass',
  'foundation', 'wall', 'floor', 'roof', 'beam', 'column',
  'slab', 'window', 'door', 'frame', 'ceiling', 'pipe',
  'scaffold', 'crane', 'equipment', 'tool', 'plant', 'machine',
  'material', 'labour', 'worker', 'contractor', 'supplier',
  'engineer', 'architect', 'client', 'manager', 'inspector',
  'foreman', 'supervisor', 'surveyor', 'plumber', 'electrician',
  'permit', 'approval', 'drawing', 'plan', 'specification',
  'document', 'report', 'certificate', 'invoice', 'quote',
  'schedule', 'programme', 'budget', 'cost', 'payment',
  'variation', 'claim', 'delay', 'progress', 'quality',
  'safety', 'risk', 'issue', 'problem', 'solution',
  'site', 'area', 'zone', 'level', 'floor', 'room',
  'section', 'building', 'structure', 'office', 'yard',
]);

/**
 * Calculate response time metrics for a participant.
 * IMPORTANT: This function must receive ALL messages (not filtered),
 * so it can properly calculate response times between different participants.
 */
function calculateResponseMetrics(
  allMessages: WhatsAppMessage[],
  participant: string
): ResponseTimeMetrics {
  // Sort all messages chronologically
  const sorted = [...allMessages].sort(
    (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
  );

  const responseTimes: number[] = [];

  for (let i = 1; i < sorted.length; i++) {
    const current = sorted[i];
    const previous = sorted[i - 1];

    // We're looking for when this participant responds to someone else
    if (current.sender === participant && previous.sender !== participant) {
      const gap = current.timestamp.getTime() - previous.timestamp.getTime();

      // Only count reasonable response times (> 0 and < 24 hours)
      const MAX_RESPONSE_TIME = 24 * 60 * 60 * 1000; // 24 hours
      if (gap > 0 && gap < MAX_RESPONSE_TIME) {
        responseTimes.push(gap);
      }
    }
  }

  if (responseTimes.length === 0) {
    return {
      averageResponseTime: 0,
      medianResponseTime: 0,
      fastestResponse: 0,
      slowestResponse: 0,
      responseCount: 0,
    };
  }

  // Sort for median calculation
  responseTimes.sort((a, b) => a - b);

  const sum = responseTimes.reduce((a, b) => a + b, 0);
  const avg = sum / responseTimes.length;
  const median = responseTimes[Math.floor(responseTimes.length / 2)];
  const fastest = responseTimes[0];
  const slowest = responseTimes[responseTimes.length - 1];

  return {
    averageResponseTime: avg,
    medianResponseTime: median,
    fastestResponse: fastest,
    slowestResponse: slowest,
    responseCount: responseTimes.length,
  };
}

/**
 * Main analysis function to extract all relevant stats from a list of WhatsApp messages.
 */
export async function analyzeChatData(messages: WhatsAppMessage[]): Promise<ChatAnalysis> {
  if (!messages?.length) {
    throw new Error('No messages provided for analysis');
  }

  const totalMessages = messages.length;
  const participants = new Set(messages.map((m) => m.sender));
  const activeParticipants = participants.size;

  // Calculate participant stats with proper response metrics
  const participantStats: ParticipantStats[] = Array.from(participants).map((name) => {
    const userMessages = messages.filter((m) => m.sender === name);
    return {
      name,
      messageCount: userMessages.length,
      firstMessage: userMessages[0]?.timestamp,
      lastMessage: userMessages[userMessages.length - 1]?.timestamp,
      // Pass ALL messages for correct response time calculation
      responseMetrics: calculateResponseMetrics(messages, name),
    };
  });
  participantStats.sort((a, b) => b.messageCount - a.messageCount);

  const activityPatterns = analyzeActivityPatterns(messages);
  const decisions = extractProjectDecisionsWithConfidence(messages);
  const messagesByDate = aggregateMessagesByDate(messages);
  const keyTopics = analyzeKeyTopics(messages);
  const delayAnalysis = analyzeDelays(messages);
  const additionalWorkAnalysis = analyzeAdditionalWork(messages);

  // Calculate parsing stats from messages
  const parsingStats: ParsingStats = {
    totalLines: messages.length, // Best estimate without original lines
    parsedMessages: messages.length,
    skippedLines: 0,
    systemMessages: messages.filter((m) => m.isSystemMessage).length,
    mediaMessages: messages.filter((m) => m.messageType === 'media').length,
    platform: 'unknown',
  };

  return {
    totalMessages,
    activeParticipants,
    peakActivityTime: activityPatterns.peakHour,
    keyTopics,
    messagesByDate,
    participantStats,
    decisions,
    activityPatterns,
    delayAnalysis,
    additionalWorkAnalysis,
    parsingStats,
    messages: messages.map((msg) => ({
      ...msg,
      dateTime: format(msg.timestamp, 'dd MMM yyyy HH:mm'),
    })),
  };
}

function analyzeActivityPatterns(messages: WhatsAppMessage[]) {
  const hourlyActivity = new Array(24).fill(0);
  const dailyActivity = new Map<string, number>();

  let maxActivityCount = 0;
  let peakHourIndex = 0;

  messages.forEach((msg) => {
    const hour = msg.timestamp.getHours();
    const day = msg.timestamp.toISOString().split('T')[0];

    hourlyActivity[hour] += 1;
    if (hourlyActivity[hour] > maxActivityCount) {
      maxActivityCount = hourlyActivity[hour];
      peakHourIndex = hour;
    }

    dailyActivity.set(day, (dailyActivity.get(day) || 0) + 1);
  });

  const nextHourIndex = (peakHourIndex + 1) % 24;
  const formatHour = (h: number) => h.toString().padStart(2, '0') + ':00';
  const peakHour = `${formatHour(peakHourIndex)} - ${formatHour(nextHourIndex)}`;

  const peakDayEntry = Array.from(dailyActivity.entries()).sort((a, b) => b[1] - a[1])[0];
  const peakDay = peakDayEntry?.[0] ?? null;

  return {
    hourlyActivity,
    peakHour,
    peakDay,
  };
}

/**
 * Extract project decisions with context-aware confidence scoring.
 * Uses regex patterns to reduce false positives.
 */
function extractProjectDecisionsWithConfidence(messages: WhatsAppMessage[]): Decision[] {
  const decisions: Decision[] = [];

  for (const msg of messages) {
    const content = msg.content;

    // Check each category's context-aware patterns
    for (const [type, patterns] of Object.entries(KEYWORD_PATTERNS)) {
      for (const { pattern, confidence } of patterns) {
        if (pattern.test(content)) {
          decisions.push({
            date: msg.timestamp,
            sender: msg.sender,
            content: msg.content,
            type: type as DecisionType,
            confidence,
          });
          break; // Only add once per category per message
        }
      }
    }
  }

  // Sort by date descending, then by confidence (high first)
  const confidenceOrder: Record<ConfidenceLevel, number> = { high: 0, medium: 1, low: 2 };
  decisions.sort((a, b) => {
    const dateCompare = b.date.getTime() - a.date.getTime();
    if (dateCompare !== 0) return dateCompare;
    return confidenceOrder[a.confidence] - confidenceOrder[b.confidence];
  });

  return decisions;
}

function analyzeKeyTopics(messages: WhatsAppMessage[]): string[] {
  const wordFrequency = new Map<string, number>();

  for (const msg of messages) {
    const words = msg.content
      .split(/\s+/)
      .map((w) => w.replace(/[.,!?]+$/, ''))
      .filter((w) => isValidTopicWord(w));

    for (const w of words) {
      wordFrequency.set(w, (wordFrequency.get(w) || 0) + 1);
    }
  }

  return Array.from(wordFrequency.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([word]) => word);
}

function isValidTopicWord(word: string): boolean {
  const lower = word.toLowerCase();

  if (lower.length < 4) return false;
  if (NON_NOUNS.has(lower)) return false;
  if (/^[0-9]+$/.test(lower)) return false;

  if (PROJECT_NOUNS.has(lower)) return true;
  if (NOUN_ENDINGS.some((ending) => lower.endsWith(ending))) return true;
  if (word[0] === word[0].toUpperCase() && word.length > 1) return true;

  return false;
}

function aggregateMessagesByDate(messages: WhatsAppMessage[]) {
  const dailyMessages = new Map<string, { count: number; topics: Set<string> }>();

  for (const msg of messages) {
    const date = msg.timestamp.toISOString().split('T')[0];
    const foundTopics = extractTopicsFromMessage(msg.content.toLowerCase());

    if (!dailyMessages.has(date)) {
      dailyMessages.set(date, { count: 0, topics: new Set() });
    }

    const day = dailyMessages.get(date)!;
    day.count++;
    foundTopics.forEach((t) => day.topics.add(t));
  }

  return Array.from(dailyMessages.entries())
    .map(([date, { count, topics }]) => ({
      date,
      count,
      topics: Array.from(topics),
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

function extractTopicsFromMessage(content: string): string[] {
  const categories: string[] = [];

  for (const [category, terms] of Object.entries(PROJECT_TERMS)) {
    if (terms.some((term) => content.includes(term))) {
      categories.push(category);
    }
  }

  return categories;
}

function analyzeDelays(messages: WhatsAppMessage[]) {
  const delays: { date: Date; duration?: number; reason?: string }[] = [];
  const delayTypes = new Map<string, number>();

  for (const msg of messages) {
    const content = msg.content.toLowerCase();
    const hasDelayKeywords = PROJECT_TERMS.delays.some((term) => content.includes(term));
    if (!hasDelayKeywords) continue;

    const duration = extractDelayDuration(content);
    const reason = identifyDelayReason(content);
    if (reason) {
      delayTypes.set(reason, (delayTypes.get(reason) || 0) + 1);
    }

    delays.push({ date: msg.timestamp, duration, reason });
  }

  const delayTrend = aggregateByDate(delays.map((d) => d.date));

  const validDurations = delays.filter((d) => d.duration).map((d) => d.duration!);
  const averageDuration =
    validDurations.reduce((acc, val) => acc + val, 0) / (validDurations.length || 1);

  return {
    totalDelays: delays.length,
    averageDelayDuration: averageDuration || 0,
    delaysByType: Object.fromEntries(delayTypes),
    delayTrend,
    mostCommonReasons: Array.from(delayTypes.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([reason]) => reason),
  };
}

function extractDelayDuration(content: string): number | undefined {
  const match = content.match(/(\d+)\s*(day|week|month)s?/);
  if (!match) return undefined;

  const [, amountStr, unit] = match;
  const amount = parseInt(amountStr, 10);
  if (Number.isNaN(amount)) return undefined;

  switch (unit) {
    case 'week':
      return amount * 7;
    case 'month':
      return amount * 30;
    default:
      return amount;
  }
}

function identifyDelayReason(content: string): string {
  for (const [reasonCategory, terms] of Object.entries(DELAY_REASONS)) {
    if (terms.some((term) => content.includes(term))) {
      return reasonCategory;
    }
  }
  return 'other';
}

function analyzeAdditionalWork(messages: WhatsAppMessage[]) {
  const additionalWork: {
    date: Date;
    value?: number;
    type?: string;
    status?: 'approved' | 'pending' | 'rejected';
  }[] = [];

  let totalValue = 0;
  const typeBreakdown = new Map<string, number>();
  const approvalStatus = { approved: 0, pending: 0, rejected: 0 };

  for (const msg of messages) {
    const content = msg.content.toLowerCase();
    if (!content.includes('variation') && !content.includes('additional work')) continue;

    const valueMatch = content.match(/[£$€](\d+(?:,\d{3})*(?:\.\d{2})?)/);
    const numericValue = valueMatch
      ? parseFloat(valueMatch[1].replace(/,/g, ''))
      : undefined;

    const extraType = identifyWorkType(content);
    if (extraType) {
      typeBreakdown.set(extraType, (typeBreakdown.get(extraType) || 0) + 1);
    }

    let status: 'approved' | 'pending' | 'rejected' = 'pending';
    if (content.includes('approved')) status = 'approved';
    if (content.includes('rejected')) status = 'rejected';
    approvalStatus[status]++;

    if (numericValue) {
      totalValue += numericValue;
    }

    additionalWork.push({
      date: msg.timestamp,
      value: numericValue,
      type: extraType,
      status,
    });
  }

  const valueByMonth = aggregateValueByMonth(additionalWork);

  return {
    totalItems: additionalWork.length,
    totalValue,
    approvalStatus,
    typeBreakdown: Object.fromEntries(typeBreakdown),
    valueByMonth,
  };
}

function identifyWorkType(content: string): string {
  for (const [category, terms] of Object.entries(WORK_TYPES)) {
    if (terms.some((term) => content.includes(term))) {
      return category;
    }
  }
  return 'other';
}

function aggregateByDate(dates: Date[]) {
  const counts = new Map<string, number>();

  for (const date of dates) {
    const dateStr = date.toISOString().split('T')[0];
    counts.set(dateStr, (counts.get(dateStr) || 0) + 1);
  }

  return Array.from(counts.entries())
    .map(([date, count]) => ({ date, count }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

function aggregateValueByMonth(items: { date: Date; value?: number }[]) {
  const monthlyValues = new Map<string, number>();

  for (const item of items) {
    if (!item.value) continue;
    const monthKey = item.date.toISOString().slice(0, 7);
    monthlyValues.set(monthKey, (monthlyValues.get(monthKey) || 0) + item.value);
  }

  return Array.from(monthlyValues.entries())
    .map(([month, value]) => ({ month, value }))
    .sort((a, b) => a.month.localeCompare(b.month));
}
